<template>
  <div class="entra-page">
    <EntraPageHeader
      title="Reports"
      :tenantDisplayName="tenantName"
      :primaryDomain="primaryDomain"
      :regionCode="regionCode"
      :tenantStatus="tenantStatus"
      :freshnessState="freshnessState"
      :lastCollection="lastCollection"
      :dataCompleteness="dataCompleteness"
      :capabilities="capabilities"
      :showSearch="false"
      :showExport="false"
      @refresh="loadData"
    />

    <div class="page-body">
      <div v-if="error" class="error-banner">{{ error }}</div>

      <div class="category-section" v-for="section in sections" :key="section.key">
        <div class="section-header">
          <svg class="section-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" v-html="section.icon"></svg>
          <h3 class="section-title">{{ section.label }}</h3>
          <span class="section-count">{{ section.count }} reports</span>
        </div>

        <div class="reports-grid">
          <div
            v-for="report in section.reports"
            :key="report.key"
            class="report-card cursor-pointer"
            :class="{ 'report-unavailable': !isAvailable(report.key) }"
            @click="generateReport(report.key)"
          >
            <div class="card-top">
              <div class="card-icon-wrapper" :class="'icon-color-' + reportIconColor(report.key)">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" v-html="report.icon"></svg>
              </div>
              <span v-if="!isAvailable(report.key)" class="status-pill status-pending">Coming Soon</span>
            </div>

            <div class="card-body">
              <h4 class="card-title">{{ report.label }}</h4>
              <p class="card-desc">{{ report.description }}</p>
            </div>

            <div class="card-footer">
              <span class="card-action" :class="{ 'card-action-loading': report.generating }">
                {{ report.generating ? 'Generating\u2026' : 'View Report' }}
                <svg v-if="!report.generating" class="arrow-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="14" height="14">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7"/>
                </svg>
              </span>
              <button class="card-export-btn" @click.stop="exportReport(report.key)" :disabled="report.generating" title="Download CSV">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="14" height="14">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="showDrawer" class="drawer-overlay" @click.self="closeDrawer">
      <div class="drawer">
        <div class="drawer-header">
          <div class="drawer-title-group">
            <div class="drawer-icon-badge" :class="'drawer-icon-' + activeReportCategory">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" v-html="activeReportIcon"></svg>
            </div>
            <div class="drawer-title-text">
              <h2>{{ activeReportLabel }}</h2>
              <span class="drawer-subtitle">Report &bull; {{ tenantName || 'Tenant' }}</span>
            </div>
          </div>
          <div class="drawer-header-actions">
            <button class="drawer-action-btn" @click="exportReport(activeReportKey)" :disabled="reportState !== 'loaded'" title="Download CSV">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="16" height="16">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </button>
            <button class="drawer-close" @click="closeDrawer">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="20" height="20">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>

        <div v-if="reportState === 'loading'" class="drawer-content">
          <div class="skeleton-header">
            <div class="skeleton skeleton-tag"></div>
            <div class="skeleton skeleton-meta"></div>
          </div>
          <div class="skeleton-table-wrap">
            <div class="skeleton skeleton-th" v-for="n in reportColumns.length || 5" :key="'th-'+n"></div>
            <div class="skeleton skeleton-tr" v-for="n in 6" :key="'tr-'+n"></div>
          </div>
        </div>

        <div v-else-if="reportState === 'unavailable'" class="drawer-content drawer-state">
          <div class="state-icon-wrapper state-icon-warn">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="40" height="40">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
          </div>
          <h3 class="state-title">Report Unavailable</h3>
          <p class="state-desc">{{ reportMessage }}</p>
        </div>

        <div v-else-if="reportState === 'empty'" class="drawer-content drawer-state">
          <div class="state-icon-wrapper state-icon-empty">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="1.5" width="40" height="40">
              <path stroke-linecap="round" stroke-linejoin="round" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"/>
            </svg>
          </div>
          <h3 class="state-title">No Data</h3>
          <p class="state-desc">No records found for this report. Try running a collection first or check back later.</p>
        </div>

        <div v-else class="drawer-content">
          <div class="drawer-toolbar">
            <div class="toolbar-stats">
              <span class="stat-pill">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="14" height="14">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16"/>
                </svg>
                {{ reportData.data.length }} {{ reportData.data.length === 1 ? 'row' : 'rows' }}
              </span>
              <span class="stat-pill stat-pill-dim">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="14" height="14">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                {{ formatTimestamp(reportData.generatedAt) }}
              </span>
            </div>
            <button class="toolbar-export-btn" @click="exportReport(activeReportKey)" title="Download CSV">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2" width="15" height="15">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
              Export CSV
            </button>
          </div>

          <div v-if="reportSummary()" class="drawer-summary">
            <div class="summary-item summary-total">
              <span class="summary-value">{{ reportSummary().total }}</span>
              <span class="summary-label">Total</span>
            </div>
            <div class="summary-item summary-critical">
              <span class="summary-value">{{ reportSummary().expired }}</span>
              <span class="summary-label">Expired</span>
            </div>
            <div class="summary-item summary-warning">
              <span class="summary-value">{{ reportSummary().expiring30 }}</span>
              <span class="summary-label">Expiring &lt;30d</span>
            </div>
            <div class="summary-item summary-caution">
              <span class="summary-value">{{ reportSummary().expiring90 }}</span>
              <span class="summary-label">Expiring &lt;90d</span>
            </div>
          </div>

          <div class="report-table-wrapper">
            <table class="report-table" v-if="reportData && reportData.data && reportData.data.length > 0">
              <thead>
                <tr>
                  <th v-for="col in reportColumns" :key="col">
                    {{ formatColumnLabel(col) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in reportData.data" :key="idx">
                  <td v-for="col in reportColumns" :key="col">
                    <span :class="cellClass(col, row[col])">{{ formatCell(row[col]) }}</span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="drawer-footer">
            <span class="footer-hint">{{ reportColumns.length }} columns &bull; {{ reportData.data.length }} {{ reportData.data.length === 1 ? 'row' : 'rows' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import { getEntraTenantSummary, getEntraTenantCapabilities } from '@/services/entra-api.js';
import { getCapabilityStateLabel } from '@/services/entra-utils.js';
import { getEntraApiAccessToken } from '@/services/auth-utils.js';

var REPORT_ICONS = {
  'identity-health': '<path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>',
  'licence-position': '<path stroke-linecap="round" stroke-linejoin="round" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01"/>',
  'licence-optimisation': '<path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>',
  'disabled-licensed': '<path stroke-linecap="round" stroke-linejoin="round" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"/>',
  'inactive-licensed': '<path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>',
  'privileged-access': '<path stroke-linecap="round" stroke-linejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>',
  'guest-review': '<path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>',
  'group-governance': '<path stroke-linecap="round" stroke-linejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"/>',
  'credential-expiry': '<path stroke-linecap="round" stroke-linejoin="round" d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"/>',
  'application-ownership': '<path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>',
  'findings-status': '<path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"/>',
  'mfa-posture': '<path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>',
  'monthly-change': '<path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>'
};

var UNAVAILABLE_REPORTS = ['mfa-posture', 'monthly-change'];

var REPORT_CATEGORIES = [
  { key: 'identity', label: 'Identity & Access', icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>' },
  { key: 'licensing', label: 'Licensing & Cost', icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>' },
  { key: 'applications', label: 'Applications', icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zm10 0a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"/>' },
  { key: 'governance', label: 'Governance', icon: '<path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>' }
];

var CATEGORY_REPORT_MAP = {
  identity: ['identity-health'],
  licensing: ['licence-position', 'licence-optimisation', 'disabled-licensed', 'inactive-licensed'],
  applications: ['credential-expiry', 'application-ownership', 'mfa-posture'],
  governance: ['privileged-access', 'guest-review', 'group-governance', 'findings-status', 'monthly-change']
};

export default {
  name: 'EntraReports',
  components: { EntraPageHeader },
  data: function() {
    return {
      tenantId: '', tenantName: '', primaryDomain: '', regionCode: '', tenantStatus: '',
      freshnessState: 'fresh', lastCollection: '', dataCompleteness: null, capabilities: [],
      error: '',
      reports: [
        { key: 'identity-health', label: 'Identity Health Summary', description: 'Overall tenant identity posture with health score breakdown', generating: false, icon: REPORT_ICONS['identity-health'] },
        { key: 'licence-position', label: 'Licence Position', description: 'Current licence inventory, consumption and availability', generating: false, icon: REPORT_ICONS['licence-position'] },
        { key: 'licence-optimisation', label: 'Licence Optimisation', description: 'All detected licence savings with financial impact', generating: false, icon: REPORT_ICONS['licence-optimisation'] },
        { key: 'disabled-licensed', label: 'Disabled Users with Licences', description: 'Users with disabled accounts consuming paid licences', generating: false, icon: REPORT_ICONS['disabled-licensed'] },
        { key: 'inactive-licensed', label: 'Inactive Licensed Users', description: 'Users with licences and no recent sign-in activity', generating: false, icon: REPORT_ICONS['inactive-licensed'] },
        { key: 'privileged-access', label: 'Privileged Access Review', description: 'Active privileged role assignments and access patterns', generating: false, icon: REPORT_ICONS['privileged-access'] },
        { key: 'guest-review', label: 'Guest Review', description: 'Guest accounts, activity and governance', generating: false, icon: REPORT_ICONS['guest-review'] },
        { key: 'group-governance', label: 'Group Governance', description: 'Ownerless, empty and governance findings for all groups', generating: false, icon: REPORT_ICONS['group-governance'] },
        { key: 'credential-expiry', label: 'Credential Expiry', description: 'Application secrets and certificates with expiry dates', generating: false, icon: REPORT_ICONS['credential-expiry'] },
        { key: 'application-ownership', label: 'Application Ownership', description: 'Applications without adequate ownership coverage', generating: false, icon: REPORT_ICONS['application-ownership'] },
        { key: 'findings-status', label: 'Findings Status', description: 'Status summary of all findings across the tenant', generating: false, icon: REPORT_ICONS['findings-status'] },
        { key: 'mfa-posture', label: 'MFA & Authentication', description: 'MFA registration and capability rates', generating: false, icon: REPORT_ICONS['mfa-posture'] },
        { key: 'monthly-change', label: 'Monthly Change Report', description: 'Month-over-month delta in users, licences and findings', generating: false, icon: REPORT_ICONS['monthly-change'] }
      ],
      showDrawer: false,
      activeReportLabel: '',
      activeReportKey: '',
      activeReportIcon: '',
      activeReportCategory: 'identity',
      reportState: 'loading',
      reportMessage: '',
      reportData: null,
      reportColumns: []
    };
  },
  computed: {
    sections: function() {
      var self = this;
      return REPORT_CATEGORIES.map(function(cat) {
        var keys = CATEGORY_REPORT_MAP[cat.key] || [];
        var rpts = keys.map(function(k) {
          return self.reports.find(function(r) { return r.key === k; });
        }).filter(Boolean);
        return { key: cat.key, label: cat.label, icon: cat.icon, reports: rpts, count: rpts.length };
      });
    }
  },
  methods: {
    isAvailable: function(key) { return UNAVAILABLE_REPORTS.indexOf(key) === -1; },
    reportIconColor: function(key) {
      for (var cat in CATEGORY_REPORT_MAP) {
        if (CATEGORY_REPORT_MAP[cat].indexOf(key) !== -1) return cat;
      }
      return 'identity';
    },
    formatTimestamp: function(ts) {
      if (!ts) return '--';
      return new Date(ts).toLocaleString();
    },
    formatColumnLabel: function(col) {
      return col.replace(/_/g, ' ').replace(/\b\w/g, function(l) { return l.toUpperCase(); });
    },
    formatCell: function(val) {
      if (val === null || val === undefined) return '\u2014';
      if (typeof val === 'boolean') return val ? 'Yes' : 'No';
      if (typeof val === 'number') return Number.isInteger(val) ? String(val) : val.toFixed(2);
      if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(val)) {
        try { return new Date(val).toLocaleDateString(); } catch(e) { /* fall through */ }
      }
      return String(val);
    },
    cellClass: function(col, val) {
      if (val === null || val === undefined) return 'cell-null';

      if (typeof val === 'boolean') return val ? 'cell-positive' : 'cell-negative';

      var lower = col.toLowerCase();
      if (lower === 'days_until_expiry' || lower === 'daysuntilexpiry') {
        if (typeof val === 'number') {
          if (val <= 0) return 'cell-critical';
          if (val <= 7) return 'cell-critical';
          if (val <= 30) return 'cell-warning';
          if (val <= 90) return 'cell-caution';
          return 'cell-ok';
        }
      }

      if (lower === 'credential_type' || lower === 'credentialtype') {
        var s = String(val).toLowerCase();
        if (s === 'secret') return 'cell-secret';
        if (s === 'certificate') return 'cell-cert';
      }

      if (lower === 'end_date_time' || lower === 'enddatetime') {
        if (typeof val === 'string' && val.length > 0) {
          try {
            var d = new Date(val);
            var diff = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
            if (diff <= 0) return 'cell-critical';
            if (diff <= 30) return 'cell-warning';
          } catch(e) { /* ignore invalid date */ }
        }
      }

      return '';
    },
    reportSummary: function() {
      if (!this.reportData || !this.reportData.data) return null;
      var key = this.activeReportKey;
      if (key === 'credential-expiry' || key === 'application-ownership') {
        var rows = this.reportData.data;
        var expired = 0, expiring30 = 0, expiring90 = 0;
        for (var i = 0; i < rows.length; i++) {
          var d = rows[i].days_until_expiry || rows[i].daysUntilExpiry;
          if (d === null || d === undefined) continue;
          if (d <= 0) expired++;
          else if (d <= 30) expiring30++;
          else if (d <= 90) expiring90++;
        }
        return { expired: expired, expiring30: expiring30, expiring90: expiring90, total: rows.length };
      }
      return null;
    },
    loadData: function() {
      var self = this;
      this.tenantId = this.$route.params.tenantId || '';
      if (!this.tenantId) {
        this.error = 'Please select a tenant from the Portfolio page first.';
        return;
      }

      getEntraTenantSummary(this.tenantId).then(function(data) {
        var t = data.tenant || data;
        self.tenantName = t.displayName || '';
        self.primaryDomain = t.primaryDomain || '';
        self.regionCode = t.regionCode || '';
        self.tenantStatus = t.status || '';
        self.lastCollection = t.lastSuccessfulCollectionAt || '';
        self.dataCompleteness = t.dataCompleteness || null;
      }).catch(function() {});

      getEntraTenantCapabilities(this.tenantId).then(function(data) {
        self.capabilities = (data || []).map(function(c) {
          return { key: c.capabilityKey, state: c.state, stateLabel: getCapabilityStateLabel(c.state) };
        });
      }).catch(function() {});
    },
    generateReport: function(key) {
      if (!this.isAvailable(key)) return;
      var self = this;
      var report = this.reports.find(function(r) { return r.key === key; });
      if (!report) return;
      report.generating = true;

      this.activeReportLabel = report.label;
      this.activeReportKey = key;
      this.activeReportIcon = report.icon;
      this.activeReportCategory = this.reportIconColor(key);
      this.reportState = 'loading';
      this.reportData = null;
      this.showDrawer = true;

      getEntraApiAccessToken().then(function(token) {
        var apiBase = process.env.VUE_APP_ENTRA_API_BASE_URL || 'http://localhost:3000';
        return fetch(apiBase + '/api/v1/entra/tenants/' + encodeURIComponent(self.tenantId) + '/reports/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
          },
          body: JSON.stringify({ reportKey: key })
        });
      }).then(function(r) { return r.json(); })
        .then(function(data) {
          report.generating = false;
          if (data.status === 'unavailable') {
            self.reportState = 'unavailable';
            self.reportMessage = data.message || 'This report is not yet available.';
            return;
          }
          self.reportData = data;
          if (data.data) {
            var rows = Array.isArray(data.data) ? data.data : [data.data];
            if (rows.length > 0) {
              self.reportColumns = Object.keys(rows[0]);
              self.reportData.data = rows;
              self.reportState = 'loaded';
            } else {
              self.reportColumns = [];
              self.reportState = 'empty';
            }
          } else {
            self.reportColumns = [];
            self.reportState = 'empty';
          }
        }).catch(function(err) {
          report.generating = false;
          self.reportState = 'unavailable';
          self.reportMessage = err.message || 'Failed to generate report.';
        });
    },
    exportReport: function(key) {
      if (!this.isAvailable(key)) return;
      var self = this;
      getEntraApiAccessToken().then(function(token) {
        var apiBase = process.env.VUE_APP_ENTRA_API_BASE_URL || 'http://localhost:3000';
        return fetch(apiBase + '/api/v1/entra/tenants/' + encodeURIComponent(self.tenantId) + '/reports/generate', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token
          },
          body: JSON.stringify({ reportKey: key })
        });
      }).then(function(r) { return r.json(); })
        .then(function(data) {
          if (data.status === 'unavailable' || !data.data) { return; }
          var rows = Array.isArray(data.data) ? data.data : [data.data];
          if (rows.length === 0) return;
          var cols = Object.keys(rows[0]);
          var csv = cols.join(',') + '\n';
          csv += rows.map(function(row) {
            return cols.map(function(c) {
              var v = row[c];
              if (v === null || v === undefined) return '';
              return String(v).includes(',') || String(v).includes('"') ? '"' + String(v).replace(/"/g, '""') + '"' : String(v);
            }).join(',');
          }).join('\n');
          var blob = new Blob([csv], { type: 'text/csv' });
          var url = URL.createObjectURL(blob);
          var a = document.createElement('a');
          a.href = url;
          a.download = key + '-' + new Date().toISOString().slice(0,10) + '.csv';
          a.click();
          URL.revokeObjectURL(url);
        }).catch(function() {});
    },
    closeDrawer: function() {
      this.showDrawer = false;
    }
  },
  mounted: function() { this.loadData(); },
  watch: { '$route.params.tenantId': 'loadData' }
};
</script>

<style scoped>
.entra-page { min-height: 100%; }
.page-body { padding: 20px 24px; }

.error-banner {
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  color: #991b1b;
  font-size: 0.8125rem;
  margin-bottom: 20px;
}

.category-section { margin-bottom: 32px; }
.category-section:last-child { margin-bottom: 0; }

.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e2e8f0;
}
.section-icon { width: 20px; height: 20px; color: #64748b; flex-shrink: 0; }
.section-title { font-size: 0.875rem; font-weight: 600; color: #1e293b; margin: 0; flex: 1; }
.section-count { font-size: 0.6875rem; color: #94a3b8; font-weight: 500; }

.reports-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 12px;
}

.report-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  transition: border-color 0.15s, box-shadow 0.15s;
  cursor: pointer;
}
.report-card:hover { border-color: #93c5fd; box-shadow: 0 1px 6px rgba(37,99,235,0.08); }
.report-card.report-unavailable { opacity: 0.55; cursor: default; }
.report-card.report-unavailable:hover { border-color: #e2e8f0; box-shadow: none; }

.card-top { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 12px; }

.card-icon-wrapper {
  width: 36px; height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.card-icon-wrapper svg { width: 20px; height: 20px; }
.icon-color-identity { background: #dbeafe; color: #1E40AF; }
.icon-color-licensing { background: #d1fae5; color: #047857; }
.icon-color-applications { background: #ede9fe; color: #7C3AED; }
.icon-color-governance { background: #fef3c7; color: #B45309; }

.status-pill {
  font-size: 0.625rem;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}
.status-pending { background: #f1f5f9; color: #64748b; }

.card-body { flex: 1; margin-bottom: 14px; }
.card-title { font-size: 0.8125rem; font-weight: 600; color: #1e293b; margin: 0 0 4px 0; line-height: 1.3; }
.card-desc { font-size: 0.6875rem; color: #64748b; margin: 0; line-height: 1.4; }

.card-footer { display: flex; align-items: center; justify-content: space-between; }
.card-action {
  font-size: 0.75rem;
  font-weight: 600;
  color: #1E40AF;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: color 0.15s;
}
.report-card:hover .card-action { color: #1d4ed8; }
.card-action-loading { color: #94a3b8; }
.arrow-icon { transition: transform 0.15s; }
.report-card:hover .arrow-icon { transform: translateX(2px); }

.card-export-btn {
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 4px 8px;
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  display: flex;
  align-items: center;
}
.card-export-btn:hover { color: #475569; border-color: #cbd5e1; background: #f8fafc; }
.card-export-btn:disabled { opacity: 0.4; cursor: not-allowed; }

.drawer-overlay { position: fixed; top: 0; right: 0; bottom: 0; left: 0; background: rgba(15,23,42,0.4); z-index: 100; display: flex; justify-content: flex-end; backdrop-filter: blur(2px); }
.drawer { width: 760px; max-width: 94vw; height: 100%; background: #ffffff; overflow-y: auto; box-shadow: -12px 0 40px rgba(15,23,42,0.15); animation: drawer-slide 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
@keyframes drawer-slide { from { transform: translateX(60px); opacity: 0.6; } to { transform: translateX(0); opacity: 1; } }

.drawer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(8px);
  z-index: 1;
}
.drawer-title-group { display: flex; align-items: center; gap: 12px; }
.drawer-icon-badge {
  width: 40px; height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.drawer-icon-badge svg { width: 22px; height: 22px; }
.drawer-icon-identity { background: #dbeafe; color: #1E40AF; }
.drawer-icon-licensing { background: #d1fae5; color: #047857; }
.drawer-icon-applications { background: #ede9fe; color: #7C3AED; }
.drawer-icon-governance { background: #fef3c7; color: #B45309; }
.drawer-title-text { display: flex; flex-direction: column; gap: 2px; }
.drawer-title-text h2 { margin: 0; font-size: 1rem; font-weight: 600; color: #0f172a; }
.drawer-subtitle { font-size: 0.6875rem; color: #94a3b8; font-weight: 500; }
.drawer-header-actions { display: flex; align-items: center; gap: 6px; }
.drawer-action-btn {
  background: none; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 8px; color: #94a3b8; cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  display: flex; align-items: center;
}
.drawer-action-btn:hover { color: #475569; border-color: #cbd5e1; background: #f8fafc; }
.drawer-action-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.drawer-close {
  background: none; border: none; color: #94a3b8; cursor: pointer;
  padding: 6px; border-radius: 8px;
  transition: color 0.15s, background 0.15s;
  display: flex;
}
.drawer-close:hover { color: #475569; background: #f1f5f9; }

.drawer-content { padding: 0; }
.drawer-state {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  min-height: 360px; gap: 8px; padding: 48px 24px;
}
.state-icon-wrapper { width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin-bottom: 8px; }
.state-icon-warn { background: #fef3c7; color: #B45309; }
.state-icon-empty { background: #f1f5f9; color: #94a3b8; }
.state-title { font-size: 1rem; font-weight: 600; color: #1e293b; margin: 0; }
.state-desc { font-size: 0.8125rem; color: #64748b; margin: 0; text-align: center; max-width: 320px; }

.drawer-toolbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 24px; border-bottom: 1px solid #f1f5f9;
}
.toolbar-stats { display: flex; align-items: center; gap: 8px; }
.stat-pill {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 0.6875rem; font-weight: 600; color: #475569;
  background: #f1f5f9; padding: 4px 10px; border-radius: 8px;
}
.stat-pill-dim { color: #94a3b8; background: transparent; padding: 4px 0; }
.stat-pill svg { opacity: 0.6; }
.toolbar-export-btn {
  background: #ffffff; border: 1px solid #e2e8f0; border-radius: 8px;
  padding: 6px 14px; font-size: 0.75rem; font-weight: 500; color: #475569;
  cursor: pointer; display: inline-flex; align-items: center; gap: 6px;
  transition: color 0.15s, border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: 'Inter', sans-serif;
}
.toolbar-export-btn:hover { color: #1e293b; border-color: #cbd5e1; background: #f8fafc; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }

.drawer-summary {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px;
  padding: 14px 24px; border-bottom: 1px solid #f1f5f9;
}
.summary-item {
  display: flex; flex-direction: column; align-items: center; gap: 2px;
  padding: 10px 8px; border-radius: 8px;
}
.summary-value { font-size: 1.25rem; font-weight: 700; line-height: 1; }
.summary-label { font-size: 0.625rem; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; }
.summary-total { background: #f8fafc; }
.summary-total .summary-value { color: #1e293b; }
.summary-total .summary-label { color: #64748b; }
.summary-critical { background: #fef2f2; }
.summary-critical .summary-value { color: #dc2626; }
.summary-critical .summary-label { color: #991b1b; }
.summary-warning { background: #fffbeb; }
.summary-warning .summary-value { color: #d97706; }
.summary-warning .summary-label { color: #92400e; }
.summary-caution { background: #f0fdf4; }
.summary-caution .summary-value { color: #059669; }
.summary-caution .summary-label { color: #047857; }

.report-table-wrapper { margin: 0 24px 16px; border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; }
.report-table { width: 100%; border-collapse: collapse; font-size: 0.75rem; }
.report-table th {
  background: #f8fafc; color: #475569; font-weight: 600; text-align: left;
  padding: 9px 14px; border-bottom: 2px solid #e2e8f0; white-space: nowrap;
  text-transform: capitalize; font-size: 0.6875rem; letter-spacing: 0.03em;
  position: sticky; top: 0;
}
.report-table td {
  padding: 8px 14px; border-bottom: 1px solid #f1f5f9; color: #334155;
  white-space: nowrap; max-width: 200px; overflow: hidden; text-overflow: ellipsis;
}
.report-table tbody tr { transition: background 0.08s; }
.report-table tbody tr:hover { background: #f8fafc; }
.report-table tbody tr:last-child td { border-bottom: none; }

.cell-null { color: #cbd5e1; }
.cell-positive { color: #047857; font-weight: 600; }
.cell-negative { color: #dc2626; font-weight: 600; }
.cell-critical { color: #dc2626; font-weight: 700; background: #fef2f2; padding: 1px 6px; border-radius: 4px; display: inline-block; }
.cell-warning { color: #d97706; font-weight: 600; background: #fffbeb; padding: 1px 6px; border-radius: 4px; display: inline-block; }
.cell-caution { color: #059669; }
.cell-ok { color: #047857; }
.cell-secret { color: #d97706; font-weight: 600; background: #fffbeb; padding: 1px 6px; border-radius: 4px; display: inline-block; text-transform: capitalize; }
.cell-cert { color: #2563eb; font-weight: 600; background: #eff6ff; padding: 1px 6px; border-radius: 4px; display: inline-block; text-transform: capitalize; }

.drawer-footer {
  padding: 10px 24px; border-top: 1px solid #f1f5f9;
  display: flex; justify-content: flex-end;
}
.footer-hint { font-size: 0.6875rem; color: #cbd5e1; }

.skeleton-header { padding: 14px 24px; border-bottom: 1px solid #f1f5f9; display: flex; gap: 10px; }
.skeleton-tag { width: 80px; height: 24px; border-radius: 8px; }
.skeleton-meta { width: 180px; height: 24px; border-radius: 8px; }
.skeleton-table-wrap { padding: 0 24px; margin-top: 4px; }
.skeleton-th { height: 32px; margin-bottom: 1px; border-radius: 0; }
.skeleton-tr { height: 36px; margin-bottom: 1px; border-radius: 0; }
.skeleton { background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%); background-size: 200% 100%; animation: shimmer 1.5s ease-in-out infinite; border-radius: 4px; }
@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

</style>
